import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InsuredTrip, InsuredTrips } from '@shared/models/policies/detail/insured-trip/insured-trip';
import { Observable } from 'rxjs';
import { CommonService } from './common.service';

import insuredTrips from '@mocks/insured-trips.json';
import { catchError, map } from 'rxjs/operators';
import { DateFormatPipe } from '@core/pipes/date-format.pipe';
import { MaInsurance, MaInsuranceTravelRZCP } from '@shared/models/ma-insurance';

import maInsurance from '@mocks/maInsurancePO.json';
import { InsuredTripRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-request';
import { InsuredTripResponse } from '@shared/models/policies/detail/insured-trip/insured-trip-response';
import { InsuredTripDetail } from '@shared/models/policies/detail/insured-trip/insured-trip-detail';

import insuredTripDetail from '@mocks/insured-trip-detail.json';
import { InsuredTripCancelRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-cancel-request';
import { InsuredTripCancelResponse } from '@shared/models/policies/detail/insured-trip/insured-trip-cancel-response';
import { InsuredTripUpdateRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-update-request';
import { InsuredTripSummary } from '@shared/models/policies/detail/insured-trip/insured-trip-summary';


import insuredTripResponse from '@mocks/insured-trips-response.json';
import insuredTripSearch from '@mocks/insured-trips-search.json';
import travelStatusIds from '@mocks/travelStatusIds.json';

import { InsuredTripTravelCardRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-travel-card-request';
import { InsuredTripTravelCardResponse } from '@shared/models/policies/detail/insured-trip/insured-trip-travel-card-response';
import { InsuredTripChangeEndDateRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-change-end-date-request';
import { InsuredTripChangeEndDateResponse } from '@shared/models/policies/detail/insured-trip/insured-trip-change-end-date-response';
import { TravelStatusIds } from '@shared/models/policies/detail/travel-status-ids';
import { InsuredTripListRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-list-request';
import { WorkerDataResponse } from '@shared/models/worker/worker-data-response';
import { WorkerDone } from '@shared/models/worker/worker-done';


@Injectable({
    providedIn: 'root'
})
export class TravelMemberService {

    private insuredTrips: InsuredTrips;
    private insuredTripDetail: InsuredTripDetail;
    private maInsurance: MaInsuranceTravelRZCP;
    private insuredTripSearch: InsuredTrips;
    private travelStatusIds: TravelStatusIds;
    private insuredTripResponse: InsuredTripResponse;

    constructor(
        private commonService: CommonService,
        private http: HttpClient,
        private dateFormatPipe: DateFormatPipe,
    ) {
        this.insuredTrips = insuredTrips;
        this.maInsurance = maInsurance;
        this.insuredTripDetail = insuredTripDetail;
        this.insuredTripSearch = insuredTripSearch;
        this.travelStatusIds = travelStatusIds;
        this.insuredTripResponse = insuredTripResponse;
    }

    getTravelMaInsurance(policyNumber: string): Observable<MaInsuranceTravelRZCP> {
        if (this.commonService.useMocks) {
            return new Observable(observer => observer.next(this.maInsurance));
        }

        return this.http.get<MaInsuranceTravelRZCP>(this.commonService.getServiceUrl(`ma-insurance/${policyNumber}`));
    }

    postTravelMemberList(policyNumber: string, searchData: InsuredTripListRequest): Observable<InsuredTrips> {
        if (this.commonService.useMocks) {
            return new Observable(observer => observer.next(this.insuredTrips));
        }

        return this.http.post<InsuredTrips>(this.commonService.getServiceUrl(`travel-member/search/${policyNumber}`), searchData);
    }

    getTravelMemberDetail(policyNumber: string, travelMemberId: string): Observable<InsuredTripDetail> {
        if (this.commonService.useMocks) {
            return new Observable(observer => observer.next(this.insuredTripDetail));
        }

        return this.http.get<InsuredTripDetail>(this.commonService.getServiceUrl(`travel-member/detail/${policyNumber}/${travelMemberId}`));
    }

    insertTravelMember(policyNumber: string, insuredTrip: InsuredTripRequest): Observable<InsuredTripResponse> {
        if (this.commonService.useMocks) {
            return new Observable(observer => observer.next(this.insuredTripResponse));
        }
        return this.http.post<InsuredTripResponse>(this.commonService.getServiceUrl(`travel-member/${policyNumber}`), insuredTrip);
    }

    cancelTravelMember(policyNumber: string, travelMemberId: string, insuredTripCancelRequest: InsuredTripCancelRequest): Observable<InsuredTripCancelResponse> {
        return this.http.post<InsuredTripCancelResponse>(
            this.commonService.getServiceUrl(`travel-member/cancel/${policyNumber}/${travelMemberId}`), insuredTripCancelRequest);
    }

    updateTravelMember(policyNumber: string, travelMemberId: string, insuredTripUpdateRequest: InsuredTripUpdateRequest): Observable<InsuredTripResponse> {
        return this.http.post<InsuredTripResponse>(
            this.commonService.getServiceUrl(`travel-member/update/${policyNumber}/${travelMemberId}`), insuredTripUpdateRequest);
    }
    
    generateTravelCard(insuredTripTravelCardRequest: InsuredTripTravelCardRequest): Observable<InsuredTripTravelCardResponse> {
        return this.http.post<InsuredTripTravelCardResponse>(this.commonService.getServiceUrl('generatePdf'), insuredTripTravelCardRequest);
    }

    getTravelCart(uuid: string, download: boolean = false) {

        let dl = '';
        if (download) {
            dl = '/download';
        }

        window.open(this.commonService.getServiceUrl(`generatePdf/${uuid}${dl}`), '_blank');
    }

    changeEndDate(insuredTripChangeEndDateRequest: InsuredTripChangeEndDateRequest): Observable<InsuredTripChangeEndDateResponse> {
        return this.http.post<InsuredTripChangeEndDateRequest>(this.commonService.getServiceUrl('travel-member/change-end-date'), insuredTripChangeEndDateRequest);
    }

    getTravelStatusIds(): Observable<TravelStatusIds> {
        if (this.commonService.useMocks) {
            return new Observable(observer => observer.next(travelStatusIds))
        }
        return this.http.get<TravelStatusIds>(this.commonService.getServiceUrl('travel-member/travel-status-ids'));
    }

    getWorkerData(policyNumber: string): Observable<WorkerDataResponse> {
        return this.http.get<WorkerDataResponse>(this.commonService.getServiceUrl(`travel-member/worker/${policyNumber}`));
    }

    updateWorkerDone(data: WorkerDone): Observable<WorkerDone>{
        return this.http.post<WorkerDone>(this.commonService.getServiceUrl('travel-member/worker-update-done'), data);
    }

}
